import React, { useEffect, useState } from 'react';
import ScrollContainer from 'react-indiana-drag-scroll';
import api from "../../../services/api";
import tick from "../../../assets/svg/tick.svg";
import hourglass from "../../../assets/svg/hourglass.svg";
import close from "../../../assets/svg/close.svg";

const STATIC_STEPS = [
  { status: 1, label: 'Design Created' },
  { status: 2, label: 'Saved' },
  { status: 7, label: 'Payment Status' },
  { status: 8, label: 'Live' },
];

const statusMap = {
  1: 'Created Ad',
  2: 'Saved',
  3: 'Picked',
  4: 'Design Completed',
  5: 'Rejected',
  6: 'Accepted',
  7: 'Ready TO Live',
  8: 'Live',
  9: 'completed'
};

const TimelineModal = ({ isOpen, row, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [timelineData, setTimelineData] = useState([]);

  useEffect(() => {
    if (isOpen) {
      const getTimeline = async () => {
        try {
          setLoading(true);
          setError(null);
          setTimelineData([]);

          const result = await api({
            url: "/alpha_cst_designer/timeline",
            method: "POST",
            responseType: "json",
            data: { creative_id: row.creative_id },
          });

          if (result.code === 200) {
            const apiData = result.data?.data || [];
            setTimelineData(apiData);
          } else {
            setError("Failed to fetch timeline data");
          }
        } catch (err) {
          setError("An error occurred while fetching timeline data.");
        } finally {
          setLoading(false);
        }
      };
      getTimeline();
    }
  }, [isOpen, row]);

  if (!isOpen) return null;

  const findApiStep = (status) => timelineData.find(item => item.status === status);

  const getDynamicSteps = () => {
    return timelineData.filter(item => !STATIC_STEPS.some(s => s.status === item.status));
  };

  const buildTimeline = () => {
    const dynamicSteps = getDynamicSteps();
    const timeline = [];
    timeline.push({
      ...STATIC_STEPS[1],
      ...findApiStep(STATIC_STEPS[1].status),
      isStatic: true,
    });
    dynamicSteps.forEach(item => {
      timeline.push({
        ...item,
        label: statusMap[item.status] || item.activity_type || 'Unknown',
        isStatic: false,
      });
    });
    timeline.push({
      ...STATIC_STEPS[2],
      ...findApiStep(STATIC_STEPS[2].status),
      isStatic: true,
    });
    timeline.push({
      ...STATIC_STEPS[3],
      ...findApiStep(STATIC_STEPS[3].status),
      isStatic: true,
    });
    return timeline;
  };

  const getStepColor = (step, idx, timelineArr) => {
    if (step.isStatic) {
      if (step.date) {
        return 'green';
      }
      if ((step.status === 7 || step.status === 8)) {
        const prevStaticIdx = timelineArr.slice(0, idx).reverse().findIndex(s => s.isStatic);
        if (prevStaticIdx !== -1) {
          const prevStep = timelineArr[idx - prevStaticIdx - 1];
          if (prevStep && prevStep.date) {
            return 'yellow';
          }
        }
      }
      return 'gray';
    } else {
      return 'green';
    }
  };

  const getStepIcon = (color, step) => {
    if (step.isStatic) {
      if (color === 'green') return tick;
      if (color === 'yellow') return hourglass;
      return hourglass;
    } else {
      if ((step.label || '').toLowerCase().includes('reject')) return close;
      return tick;
    }
  };

  const getTooltip = (step) => {
     return (step && step.message ? step.message : null);
  };

  const timelineArr = buildTimeline();

  const getCircleClass = (color) => {
    if (color === 'green') return 'circle completed';
    if (color === 'yellow') return 'circle in-progress';
    return 'circle upcoming';
  };
  const getLineClass = (color) => {
    if (color === 'green') return 'completed';
    if (color === 'yellow') return 'in-progress';
    return 'upcoming';
  };



  return (
    <>
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm"
        onClick={onClose}
      >
        <div
          className="relative max-w-6xl w-full max-h-[90vh] p-6 bg-white rounded-lg shadow-xl mx-4"
          onClick={e => e.stopPropagation()}
        >
          <div className="flex justify-between items-center mb-6 border-b pb-4">
            <div>
              <h2 className="text-2xl font-bold text-gray-800">Ad Progress Timeline</h2>
              <span className="text-red-600">* click the circle to see rejected reason</span>
            </div>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 text-3xl leading-none"
              aria-label="Close modal"
            >
              ×
            </button>
          </div>
          {loading ? (
            <div className="text-center text-gray-600 text-lg font-medium font-['Maven_Pro']">
              Loading timeline...
            </div>
          ) : error ? (
            <div className="text-center text-red-500 text-lg font-medium font-['Maven_Pro']">
              {error}
            </div>
          ) : !timelineArr || timelineArr.length === 0 ? (
            <div className="text-center text-gray-600 text-lg font-medium font-['Maven_Pro']">
              No timeline data available
            </div>
          ) : (
            <ScrollContainer>
              <div className="flex items-center justify-start bg-white py-10 px-5 rounded-lg relative h-[50vh]">
                <style>
                  {`
                    .tooltip-message {
                      visibility: hidden;
                      opacity: 0;
                      width: max-content;
                      max-width: 200px;
                      background-color: #333;
                      color: #fff;
                      text-align: center;
                      padding: 6px 10px;
                      border-radius: 5px;
                      position: absolute;
                      bottom: 100%;
                      left: 50%;
                      transform: translateX(-50%);
                      transition: opacity 0.3s ease;
                      pointer-events: none;
                      z-index: 10;
                      white-space: normal;
                      margin-bottom: 10px;
                    }

                    .tooltip-message::after {
                      content: "";
                      position: absolute;
                      top: 100%;
                      left: 50%;
                      transform: translateX(-50%);
                      border-width: 6px;
                      border-style: solid;
                      border-color: #333 transparent transparent transparent;
                    }

                    .tooltip-container:hover .tooltip-message {
                      visibility: visible;
                      opacity: 1;
                      pointer-events: auto;
                    }
                  `}
                </style>

                {timelineArr.map((step, idx) => {
                  const color = getStepColor(step, idx, timelineArr);
                  const icon = getStepIcon(color, step);
                  const tooltip = getTooltip(step);
                  const isLast = idx === timelineArr.length - 1;
                  return (
                    <div
                      key={idx}
                      className={`${getLineClass(color)}`}
                      style={{ position: 'relative', textAlign: 'center', width: 150, flexShrink: 0 }}
                    >
                      <div className={`tooltip-container ${tooltip ? 'cursor-pointer' : ''}`} style={{ position: 'relative' }}>
                        <div className={getCircleClass(color)} style={{ width: 30, height: 30, borderRadius: '50%', margin: '0 auto 10px', position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, color: 'white', border: color === 'green' ? '1px solid #28c76f' : color === 'yellow' ? '1px solid #e89331' : '1px solid #dcdcdc', background: color === 'green' ? '#fff' : color === 'yellow' ? '#fff' : '#dcdcdc' }}>
                          <img src={icon} alt={step.label} style={{ width: 14 }} />
                          {tooltip && (
                            <div className="tooltip-message">
                              {tooltip}
                            </div>
                          )}
                        </div>
                      </div>
                      <div
                        style={{ fontSize: 14, fontWeight: 500, margin: '4px 0', color: '#222', position: 'relative', display: 'inline-block', textTransform: 'uppercase' }}
                      >
                        {step.label}
                      </div>
                      <div className="round-date" style={{ fontSize: 12, color: '#888', marginBottom: 8 }}>
                        {step.date ? (
                          <>
                            {new Date(step.date * 1000).toLocaleDateString('en-IN')} <br />
                            {new Date(step.date * 1000).toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: 'Asia/Kolkata' })}
                          </>
                        ) : (
                          <div className="mb-5">
                            <div></div>
                            <div></div>
                          </div>
                        )}
                      </div>
                      {!isLast && (
                        <div style={{ content: '""', position: 'absolute', top: 15, left: '50%', width: '100%', height: color === 'yellow' ? 5 : 4, background: color === 'green' ? '#28c76f' : color === 'yellow' ? 'repeating-linear-gradient(to right,#e89331,#e89331 10px,#ffffff 10px,#ffffff 20px)' : '#ccc', zIndex: 0 }} />
                      )}
                    </div>
                  );
                })}
              </div>
            </ScrollContainer>
          )}
        </div>
      </div>
    </>
  );
};

export default TimelineModal;
